import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  model,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  DsButtonModule,
  DsFormFieldModule,
  DsLinkModule,
  DsSelectModule,
  DsSwitchModule,
  DsTooltipDirectiveModule,
  SelectListOption,
} from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { LegalEntitiesMap, LegalEntity } from '../../../../auth/user/legal-entities.model';
import { AppRouteId } from '../../../../route-data';
import { useI18n } from '../../../i18n/services/i18n.service';
import { NavigationService } from '../../../navigation/services/navigation.service';

@Component({
  selector: 'cdf-admin-control',
  imports: [
    DsSwitchModule,
    FormsModule,
    DsFormFieldModule,
    DsSelectModule,
    DsTooltipDirectiveModule,
    DsLinkModule,
    DsButtonModule,
  ],
  template: `
    @if (navigationService && showFeatureFlagNavigation()) {
      <button ds-button variant="outline" (click)="navigateToFeatureFlagPage()">
        {{ i18n('adminMode.featureFlag') }}
      </button>
    }

    <ds-switch
      [(ngModel)]="adminModeEnabled"
      [disabled]="!dpNr()"
      class="mb-2x"
      [ds-tooltip]="selectDpNrWarning()"
      [dsTooltipConfig]="{ origin: 'left', disabled: !!dpNr() }"
    >
      {{ i18n('adminMode.switchAdminModeLabel') }}
    </ds-switch>

    @if (countryOptions().length > 0) {
      <!-- COUNTRY -->
      @if (countryOptions().length === 1) {
        @if (country()) {
          <div class="text-label text-neutral">{{ i18n('adminMode.country') }}: {{ country() }}</div>
        } @else {
          <!-- Show nothing: global admin mode where country is empty -->
        }
      } @else {
        <ds-form-field>
          <label ds-label>{{ i18n('adminMode.country') }}</label>
          <ds-select
            [value]="country()"
            (valueChange)="selectCountry($event)"
            [options]="countryOptions()"
            [showClearAll]="true"
          />
        </ds-form-field>
      }
      <!--/COUNTRY -->

      <!-- DPNR -->
      <ds-form-field>
        <label ds-label>{{ i18n('adminMode.distributionPartner') }}</label>
        <ds-select
          [value]="dpNr() ?? null"
          (valueChange)="selectDpNr($event)"
          [options]="legalEntityOptions()"
          [disabled]="country() === null"
          [showClearAll]="true"
        />
      </ds-form-field>
      <!--/DPNR -->
    } @else {
      <!-- Fallback for no list of countries: text field -->
      <ds-form-field>
        <input
          ds-input
          ds-form-validation
          required
          [(ngModel)]="dpNr"
          (ngModelChange)="adminModeEnabled.set($event.length === 5)"
          maxlength="5"
          [placeholder]="i18n('adminMode.dpNrInputPlaceholder')"
        />
      </ds-form-field>
    }
  `,
  styleUrl: './admin-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminControlComponent {
  protected readonly i18n = useI18n();
  private readonly logger = inject(NGXLogger);
  protected readonly navigationService = inject(NavigationService, { optional: true });

  readonly dpNr = model.required<string | undefined>();
  readonly adminModeEnabled = model.required<boolean>();
  readonly regionalLocalEntities = input.required<LegalEntitiesMap>();
  readonly showFeatureFlagNavigation = input.required<boolean>();
  readonly closeMenu = output<void>();

  /**
   * The country chosen by the user.
   *
   * This is determined based on the dpNr (and updated if the dpNr changes),
   * but can be overridden using the dropdown.
   */
  protected readonly chosenCountry = linkedSignal<string | undefined, string | null>({
    source: this.dpNr,
    computation: (dpNr, previous) => {
      const country = this.findCountryByDpNr(dpNr, this.regionalLocalEntities());
      return country ?? previous?.value ?? null;
    },
  });

  /**
   * The country used for determining the legal entities to offer.
   *
   * If there is only one country in the region, this will be set to that country.
   * Otherwise, it will be set to the country chosen by the user/based on the dpNr.
   */
  protected readonly country = computed(() => {
    if (this.countryOptions().length === 1) {
      return this.countryOptions()[0].id;
    }

    const chosenCountry = this.chosenCountry();
    if (chosenCountry !== null && this.regionalLocalEntities().has(chosenCountry)) {
      return chosenCountry;
    }

    return null;
  });

  protected readonly countryOptions = computed<SelectListOption[]>(() =>
    Array.from(this.regionalLocalEntities().keys(), (key) => ({ id: key, label: key })),
  );

  protected readonly selectDpNrWarning = computed(() => {
    if (!this.country()) {
      return this.i18n('adminMode.selectCountryAndDpNrToEnableAdminMode');
    } else {
      return this.i18n('adminMode.selectDpNrToEnableAdminMode');
    }
  });

  protected readonly legalEntities = computed<LegalEntity[]>(() => {
    const country = this.country();
    return (country !== null && this.regionalLocalEntities().get(country)) || [];
  });

  protected readonly legalEntityOptions = computed<SelectListOption[]>(() =>
    this.legalEntities().map((entity) => ({
      id: entity.dpNr,
      label: entity.name ? `${entity.dpNr} ${entity.name}` : entity.dpNr,
    })),
  );

  protected selectCountry(value: string | string[] | null) {
    if (Array.isArray(value)) {
      this.logger.error('#CDFTF0172 AdminControlComponent received an array of countries, but only one is expected');
      return;
    }

    this.chosenCountry.set(value);

    // If the selected dpNr is no longer valid for the selected country, clear it
    if (!this.legalEntities().some((entity) => entity.dpNr === this.dpNr())) {
      this.selectDpNr(null);
    }
  }

  protected selectDpNr(value: string | string[] | null) {
    if (Array.isArray(value)) {
      this.logger.error('#CDFTF0172 AdminControlComponent received an array of dpNrs, but only one is expected');
      return;
    }

    this.dpNr.set(value || undefined);

    this.adminModeEnabled.set(!!value);
  }

  private findCountryByDpNr(dpNr: string | undefined, region: LegalEntitiesMap): string | undefined {
    if (!dpNr) return undefined;

    for (const [country, legalEntities] of region.entries()) {
      if (legalEntities.some((entity) => entity.dpNr === dpNr)) {
        return country;
      }
    }
    return undefined;
  }

  protected async navigateToFeatureFlagPage() {
    if (!this.navigationService) {
      this.logger.error(
        '#CDFTF0223 The button to navigate to the feature flag page should not be visible when not in a router context',
      );
      return;
    }
    await this.navigationService.navigateToRouteId(AppRouteId.FEATURE_FLAG);
    this.closeMenu.emit();
  }
}
